<template>
  <div v-if="show" class="ap-overlay" @click.self="$emit('close')">
    <div class="ap-modal">
      <div class="ap-scrollbar-cap is-top"></div>
      <div class="ap-scrollbar-cap is-bottom"></div>
      <div class="ap-modal-scroll">
        <div class="ap-modal-head">
          <h5 class="ap-modal-title">Edit Client</h5>
          <button class="ap-modal-close" @click="$emit('close')">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <!-- ── Section 1: Basic Info ──────────────────────── -->
        <div class="cdm-section">
          <div class="cdm-section-label">Basic Info</div>
          <div class="cdm-section-wrapper">
            <text-input v-model="form.client" label="Client" :icon="['fas', 'building']" />
            <text-input v-model="form.initial" label="Initial" :icon="['fas', 'stamp']" />
            <text-input v-model="form.full_name" label="Full Name" :icon="['fas', 'user']" />
            <select-input
              v-model="form.status"
              label="Status"
              :options="statusOptions"
              :icon="['fas', 'toggle-on']"
              placeholder="Select status…"
            />
          </div>
        </div>

        <!-- ── Section 2: Statutory Info ─────────────────── -->
        <template v-if="form.company_identity">
          <div class="cdm-section">
            <div class="cdm-section-label">Statutory Info</div>
            <div class="cdm-section-wrapper">
              <text-input v-model="form.company_identity.ssm_number_new" label="SSM Number (New)" :icon="['fas', 'id-card']" placeholder="e.g. 202301012345" />
              <text-input v-model="form.company_identity.ssm_number_old" label="SSM Number (Old)" :icon="['fas', 'id-card']" placeholder="e.g. 1234567-A" />
              <tax-input v-model="form.company_identity.tax_number" label="Tax Number" />
              <prefix-input v-model="form.company_identity.e_number" label="E Number" prefix="E" />
              <text-input v-model="form.company_identity.irb_branch" label="IRB Branch" :icon="['fas', 'landmark']" placeholder="e.g. Cheras" />
              <text-input v-model="form.company_identity.auditor" label="Auditor" :icon="['fas', 'user-tie']" placeholder="e.g. Marie J & Co" />
              <select-input
                v-model="yearEnd"
                label="Year End"
                :options="yearEndOptions"
                :icon="['fas', 'calendar-alt']"
                placeholder="Select month…"
              />
              <p v-if="yearEndChanged" class="ap-field-hint">Deadline dates will be recalculated on save.</p>
            </div>
          </div>
        </template>

        <!-- ── Section 3: Address ─────────────────────────── -->
        <div class="cdm-section">
          <div class="cdm-section-label">Address</div>
          <div class="cdm-section-wrapper">
            <div class="ap-field">
              <label>Business Address</label>
              <textarea v-model="form.business_address" class="ap-textarea" rows="3" placeholder="e.g. 123 Jalan Bukit Bintang, 55100 Kuala Lumpur" />
            </div>
            <div class="ap-field">
              <label>Mailing Address</label>
              <textarea v-model="form.mailing_address" class="ap-textarea" rows="3" placeholder="Same as business address, or P.O. Box…" />
            </div>
          </div>
        </div>

        <!-- ── Section 4: Important Dates ───────────────── -->
        <template v-if="hasCosec">
          <div class="cdm-section">
            <div class="cdm-section-label">Important Dates</div>
            <div class="cdm-section-wrapper">
              <select-input
                v-model="form.company_identity.cosec_termination_type"
                label="Cosec Termination Type"
                :options="cosecTerminationTypeOptions"
                :icon="['fas', 'sign-out-alt']"
                placeholder="Select type…"
              />
              <div class="ap-field">
                <label>Cosec Termination Date</label>
                <billing-date-picker
                  :value="form.company_identity.cosec_termination_date || ''"
                  @input="setCosecTermDate($event)"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- ── Section 5: Contacts ────────────────────────── -->
        <div class="cdm-section">
          <div class="cdm-section-label">Contacts</div>
          <div class="cdm-section-wrapper">
            <contact-cards v-model="emailCards" />
          </div>
        </div>

        <div class="ap-modal-actions">
          <button class="ap-btn ap-btn-ghost" @click="$emit('close')">Cancel</button>
          <button class="ap-btn ap-btn-primary" @click="handleSave">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { yearEndOptions, statusOptions } from '@/utils/clientOptions'
import TextInput from '@/components/form/TextInput.vue'
import SelectInput from '@/components/form/SelectInput.vue'
import ContactCards from '@/components/form/ContactCards.vue'
import BillingDatePicker from '@/components/form/BillingDatePicker.vue'
import TaxInput from '@/components/form/TaxInput.vue'
import PrefixInput from '@/components/form/PrefixInput.vue'
import bodyScrollLockMixin from '@/mixins/bodyScrollLockMixin'

export default {
  name: 'admin-edit-client-modal',
  mixins: [bodyScrollLockMixin],
  components: { TextInput, SelectInput, ContactCards, BillingDatePicker, TaxInput, PrefixInput },
  props: {
    show: { type: Boolean, default: false },
    client: { type: Object, default: () => ({}) }
  },
  emits: ['close', 'save'],
  data () {
    return {
      form: {},
      yearEnd: '',
      originalYearEnd: '',
      emailCards: [],
      yearEndOptions,
      statusOptions,
      cosecTerminationTypeOptions: [
        { value: 'Resigned', label: 'Resigned' },
        { value: 'Vacated', label: 'Vacated' }
      ]
    }
  },
  computed: {
    yearEndChanged () {
      return this.yearEnd !== '' && this.yearEnd !== this.originalYearEnd
    },
    hasCosec () {
      return Array.isArray(this.form.company_identity?.category) &&
        this.form.company_identity.category.some(c => c.name === 'cosec')
    }
  },
  watch: {
    client: {
      immediate: true,
      handler (val) {
        this.form = JSON.parse(JSON.stringify(val))
        const ye = val.company_identity?.year_end
        this.yearEnd = ye ? String(ye) : ''
        this.originalYearEnd = ye ? String(ye) : ''
        this.emailCards = Array.isArray(val.contacts)
          ? val.contacts.map(c => ({ ...c, roles: Array.isArray(c.roles) ? [...c.roles] : [] }))
          : []
        if (this.form.company_identity) {
          const ciDefaults = {
            tax_number: null,
            e_number: null,
            irb_branch: '',
            auditor: '',
            ssm_number_new: '',
            ssm_number_old: '',
            cosec_termination_type: '',
            cosec_termination_date: ''
          }
          Object.keys(ciDefaults).forEach(k => {
            if (!(k in this.form.company_identity)) {
              this.$set(this.form.company_identity, k, ciDefaults[k])
            }
          })
        }
        // Ensure address fields exist on form
        if (!('business_address' in this.form)) this.$set(this.form, 'business_address', '')
        if (!('mailing_address' in this.form)) this.$set(this.form, 'mailing_address', '')
      }
    },
    yearEnd (newYe) {
      if (!newYe || !this.form.company_identity) return
      const updatedCategory = this.buildUpdatedCategory(
        newYe,
        this.form.company_identity.category,
        this.form.company_identity.incorporation_date
      )
      this.form.company_identity = {
        ...this.form.company_identity,
        year_end: parseInt(newYe),
        category: updatedCategory
      }
    }
  },
  methods: {
    handleSave () {
      const validCards = this.emailCards.filter(c => c.name.trim() || c.email.trim() || c.phone.trim())
      this.$emit('save', { ...this.form, contacts: validCards })
    },
    setCosecTermDate (date) {
      this.$set(this.form.company_identity, 'cosec_termination_date', date)
    },
    buildUpdatedCategory (ye, existingCategory, incDate) {
      ye = parseInt(ye)
      if (!ye || !Array.isArray(existingCategory)) return existingCategory

      const LAST = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
      const addMo = (base, n) => {
        const m = ((base - 1 + n) % 12 + 12) % 12 + 1
        return { month: m, day: LAST[m] }
      }
      const shiftDays = (month, day, n) => {
        const d = new Date(2000, month - 1, day)
        d.setDate(d.getDate() + n)
        return { month: d.getMonth() + 1, day: d.getDate() }
      }
      const existingStatus = (catName, itemName) => {
        const cat = existingCategory.find(c => c.name === catName)
        const item = (cat?.sub_category || []).find(s => s.name === itemName)
        return item?.status || { prev: 'pending', current: 'pending', next: 'pending' }
      }
      return existingCategory.map(cat => {
        if (cat.name === 'tax') {
          const tax8 = addMo(ye, 8)
          const mitrs = shiftDays(tax8.month, tax8.day, 30)
          const sub1m = addMo(ye, -1)
          const sub30d = shiftDays(ye, LAST[ye], -30)
          const cp204 = (sub1m.month * 100 + sub1m.day) <= (sub30d.month * 100 + sub30d.day) ? sub1m : sub30d
          return {
            ...cat,
            sub_category: [
              { name: 'tax_filing', display_name: 'Tax Filing', date: tax8, status: existingStatus('tax', 'tax_filing') },
              { name: 'mitrs', display_name: 'MITRS', date: mitrs, status: existingStatus('tax', 'mitrs') },
              { name: 'cp204', display_name: 'CP204', date: cp204, status: existingStatus('tax', 'cp204') },
              { name: 'cp204a6', display_name: 'CP204A(6)', date: addMo(ye, 6), status: existingStatus('tax', 'cp204a6') },
              { name: 'cp204a9', display_name: 'CP204A(9)', date: addMo(ye, 9), status: existingStatus('tax', 'cp204a9') },
              { name: 'cp204a11', display_name: 'CP204A(11)', date: addMo(ye, 11), status: existingStatus('tax', 'cp204a11') }
            ]
          }
        }

        if (cat.name === 'cosec') {
          let arDate = null
          let arIncMonth = null
          let arIncDay = null
          if (incDate) {
            const d = new Date(incDate)
            arIncMonth = d.getMonth() + 1
            arIncDay = d.getDate()
            arDate = shiftDays(arIncMonth, arIncDay, 29)
          }
          const fsDate = addMo(ye, 7)

          const existingReminder = (cat.sub_category || []).find(s => s.name === 'reminder')
          const taskDate = (subCat, taskName) => {
            const t = (subCat?.tasks || []).find(t => t.name === taskName)
            return t?.date || null
          }
          const arR1 = taskDate(existingReminder, 'ar_reminder_1st') || (arIncMonth ? addMo(arIncMonth, -5) : null)
          const arR2 = taskDate(existingReminder, 'ar_reminder_2nd') || ((arIncMonth && arIncDay) ? { month: arIncMonth, day: arIncDay } : null)
          const arRL1 = taskDate(existingReminder, 'ar_reminder_late_1') || ((arIncMonth && arIncDay) ? shiftDays(arIncMonth, arIncDay, 45) : null)
          const arRL2 = taskDate(existingReminder, 'ar_reminder_late_2') || ((arIncMonth && arIncDay) ? shiftDays(arIncMonth, arIncDay, 90) : null)

          const afsR1 = shiftDays(ye, LAST[ye], 1)
          const afsR2 = addMo(ye, 5)
          const afsRL1 = shiftDays(fsDate.month, fsDate.day, 45)
          const afsRL2 = shiftDays(fsDate.month, fsDate.day, 90)

          return {
            ...cat,
            sub_category: [
              {
                name: 'compliance',
                display_name: 'Compliance',
                tasks: [
                  { name: 'ar', display_name: 'AR', date: arDate },
                  { name: 'fs', display_name: 'FS', date: fsDate }
                ]
              },
              {
                name: 'reminder',
                display_name: 'Reminder',
                tasks: [
                  { name: 'ar_reminder_1st', display_name: 'AR Reminder (1st)', date: arR1 },
                  { name: 'ar_reminder_2nd', display_name: 'AR Reminder (2nd)', date: arR2 },
                  { name: 'ar_reminder_late_1', display_name: 'AR Reminder (Late) 1', date: arRL1 },
                  { name: 'ar_reminder_late_2', display_name: 'AR Reminder (Late) 2', date: arRL2 },
                  { name: 'afs_reminder_1st', display_name: 'AFS Reminder (1st)', date: afsR1 },
                  { name: 'afs_reminder_2nd', display_name: 'AFS Reminder (2nd)', date: afsR2 },
                  { name: 'afs_reminder_late_1', display_name: 'AFS Reminder (Late 1)', date: afsRL1 },
                  { name: 'afs_reminder_late_2', display_name: 'AFS Reminder (Late 2)', date: afsRL2 }
                ]
              }
            ]
          }
        }

        if (cat.name === 'tp_reminder') {
          return {
            ...cat,
            sub_category: [
              { name: 'tp_reminder', display_name: 'TP Reminder', date: addMo(ye, 1), status: existingStatus('tp_reminder', 'tp_reminder') }
            ]
          }
        }

        if (['payroll_yearly', 'payroll_reminder', 'payroll_monthly', 'Incorporation'].includes(cat.name)) {
          return cat
        }

        if (cat.name === 'sst') {
          return {
            ...cat,
            sub_category: [
              { name: 'sst1', display_name: 'SST 1', date: addMo(ye, 1) },
              { name: 'sst2', display_name: 'SST 2', date: addMo(ye, 3) },
              { name: 'sst3', display_name: 'SST 3', date: addMo(ye, 5) },
              { name: 'sst4', display_name: 'SST 4', date: addMo(ye, 7) },
              { name: 'sst5', display_name: 'SST 5', date: addMo(ye, 9) },
              { name: 'sst6', display_name: 'SST 6', date: addMo(ye, 11) }
            ]
          }
        }

        return cat
      })
    }
  }
}
</script>
